/**
 * Voice Note Dictionary
 *
 * Browser-local hints for future voice-note transcription requests.
 *
 * Primary responsibilities:
 * - Remember corrected voice-note wording from the existing edit flow
 * - Provide compact personal dictionary hints for speech-to-text
 * - Persist corrections for signed-in users through the dictionary API
 *
 * Dependencies:
 * - app/api/voice-note-dictionary
 * - Browser localStorage
 */

const VOICE_NOTE_DICTIONARY_STORAGE_KEY = 'losi.voiceNoteDictionary.v1'
const MAX_DICTIONARY_ENTRIES = 20
const MAX_DICTIONARY_ENTRY_LENGTH = 180
const MAX_CORRECTION_SPAN_WORDS = 6

// --------------

function canUseStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

// --------------

function normalizeDictionaryEntry(value: string) {
  return value.replace(/\s+/g, ' ').trim().slice(0, MAX_DICTIONARY_ENTRY_LENGTH)
}

// --------------

function readWords(value: string) {
  return value
    .split(/\s+/)
    .map((word) => ({
      key: word.replace(/[^\p{L}\p{N}'-]/gu, '').toLowerCase(),
      text: word.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '')
    }))
    .filter((word) => word.key && word.text)
}

// --------------

function getCorrectionTerm(original: string, corrected: string) {
  const originalWords = readWords(original)
  const correctedWords = readWords(corrected)

  if (originalWords.length === 0 || correctedWords.length === 0) {
    return null
  }

  let start = 0
  while (
    start < originalWords.length &&
    start < correctedWords.length &&
    originalWords[start]?.key === correctedWords[start]?.key
  ) {
    start += 1
  }

  let originalEnd = originalWords.length - 1
  let correctedEnd = correctedWords.length - 1
  while (
    originalEnd >= start &&
    correctedEnd >= start &&
    originalWords[originalEnd]?.key === correctedWords[correctedEnd]?.key
  ) {
    originalEnd -= 1
    correctedEnd -= 1
  }

  const hasSharedContext =
    start > 0 ||
    originalEnd < originalWords.length - 1 ||
    (originalWords.length <= MAX_CORRECTION_SPAN_WORDS &&
      correctedWords.length <= MAX_CORRECTION_SPAN_WORDS)

  if (!hasSharedContext) {
    return null
  }

  const changedWords = correctedWords
    .slice(start, correctedEnd + 1)
    .slice(0, MAX_CORRECTION_SPAN_WORDS)
    .map((word) => word.text)

  if (changedWords.length === 0) {
    return null
  }

  return normalizeDictionaryEntry(changedWords.join(' '))
}

// --------------

function readDictionaryEntries() {
  if (!canUseStorage()) return []

  try {
    const raw = window.localStorage.getItem(VOICE_NOTE_DICTIONARY_STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed)
      ? parsed.flatMap((entry) =>
          typeof entry === 'string' && entry.trim()
            ? [normalizeDictionaryEntry(entry)]
            : []
        )
      : []
  } catch {
    return []
  }
}

// --------------

function writeDictionaryEntries(entries: string[]) {
  if (!canUseStorage()) return

  try {
    window.localStorage.setItem(
      VOICE_NOTE_DICTIONARY_STORAGE_KEY,
      JSON.stringify(entries.slice(0, MAX_DICTIONARY_ENTRIES))
    )
  } catch {
    // Transcription must not fail because browser storage is unavailable.
  }
}

// --------------

function persistDictionaryEntry(term: string, soundsLike?: string | null) {
  if (typeof fetch !== 'function') return

  void fetch('/api/voice-note-dictionary', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({
      notes: 'Corrected from a voice-note transcript.',
      source: 'voice_memo_correction',
      soundsLike,
      term
    })
  }).catch(() => undefined)
}

// --------------

export function getVoiceNotePersonalDictionaryHints() {
  return readDictionaryEntries().join('\n')
}

// --------------

export function rememberVoiceNoteCorrection(
  originalTranscript?: string | null,
  correctedTranscript?: string | null
) {
  const original = normalizeDictionaryEntry(originalTranscript ?? '')
  const corrected = normalizeDictionaryEntry(correctedTranscript ?? '')
  const correctionTerm = getCorrectionTerm(original, corrected)

  if (!original || !corrected || original === corrected || !correctionTerm) return

  const entries = readDictionaryEntries()
  const nextEntries = [
    correctionTerm,
    ...entries.filter((entry) => entry.toLowerCase() !== correctionTerm.toLowerCase())
  ]

  writeDictionaryEntries(nextEntries)
  persistDictionaryEntry(
    correctionTerm,
    original.length <= MAX_DICTIONARY_ENTRY_LENGTH ? original : null
  )
}
